import React from 'react';
import { Star, Quote, ShieldCheck, Building2 } from 'lucide-react';

const Testimonials = () => {
  const testimonials = [
    {
      id: 1,
      quote: "HOASP International transformed our multi-facility hospital operations. Their enterprise HMS reduced patient OPD queue wait times by 65%, automated pharmacy stock alerts, and seamlessly integrated diagnostic analyzers with digital EHRs. Their engineering team understands clinical workflows inside out.",
      author: "Dr. Rajesh Sharma",
      role: "Chief Medical Officer & Healthcare Director",
      company: "Apex Global Health Institute",
      rating: 5,
      image: "https://images.unsplash.com/photo-1622253692010-333f2da6031d?w=400&q=80",
      project: "Enterprise Healthcare HMS & NCD Platform"
    },
    {
      id: 2,
      quote: "We partnered with HOASP to build our high-concurrency cloud infrastructure and automated document workflow. The system handles over 100,000 daily active users with sub-second page latency and zero downtime. Outstanding technical expertise and execution!",
      author: "Vikram Malhotra",
      role: "VP of Enterprise Technology",
      company: "Acyutah Technologies",
      rating: 5,
      image: "https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=400&q=80",
      project: "Cloud Automation & Enterprise Platform"
    },
    {
      id: 3,
      quote: "Our e-commerce portal and mobile application traffic surged by 300% post-launch. HOASP's team delivered a lightning-fast React storefront integrated with automated logistics, real-time inventory sync, and WhatsApp customer telemetry. Truly a 5-star technology partner.",
      author: "Ananya Gupta",
      role: "Head of Digital Products & E-Commerce",
      company: "Zubelin Lifestyle Retail",
      rating: 5,
      image: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=400&q=80",
      project: "E-Commerce & Mobile Commerce Suite"
    }
  ];

  return (
    <section className="py-24 bg-gradient-to-b from-gray-950 via-slate-900 to-gray-950 relative overflow-hidden text-left">
      {/* Background Glow Orbs */}
      <div className="absolute top-1/3 left-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-1/3 right-1/4 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl pointer-events-none" />

      <div className="container mx-auto px-6 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16 max-w-3xl mx-auto">
          <div className="inline-flex items-center space-x-2 bg-amber-500/10 border border-amber-500/20 rounded-full px-4 py-1.5 mb-4">
            <ShieldCheck className="w-4 h-4 text-amber-400" />
            <span className="text-amber-400 text-sm font-semibold uppercase tracking-wider">
              Client Testimonials & Trust
            </span>
          </div>
          <h2 className="text-3xl md:text-5xl font-extrabold text-white mb-4 leading-tight">
            Loved By <span className="bg-gradient-to-r from-orange-400 via-amber-400 to-cyan-400 bg-clip-text text-transparent">Industry Leaders</span>
          </h2>
          <p className="text-gray-300 text-base md:text-lg leading-relaxed">
            See how our custom software solutions, web engineering, and enterprise cloud architectures empower businesses to scale seamlessly.
          </p>
        </div>

        {/* Testimonials Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-7xl mx-auto">
          {testimonials.map((t) => (
            <div
              key={t.id}
              className="bg-white border border-gray-100 hover:border-cyan-400 rounded-3xl p-8 flex flex-col justify-between shadow-xl hover:shadow-2xl hover:shadow-cyan-500/15 transition-all duration-300 hover:-translate-y-1.5 group"
            >
              <div>
                {/* Header Info & Rating */}
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center space-x-1">
                    {[...Array(t.rating)].map((_, i) => (
                      <Star key={i} className="w-4.5 h-4.5 fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                  <Quote className="w-8 h-8 text-orange-500/40 group-hover:text-cyan-500 transition-colors" />
                </div>

                {/* Quote Text */}
                <p className="text-gray-700 text-sm leading-relaxed mb-6 italic font-medium">
                  "{t.quote}"
                </p>
              </div>

              {/* Author & Project info */}
              <div className="pt-6 border-t border-gray-100">
                <div className="flex items-center space-x-4 mb-4">
                  <img
                    src={t.image}
                    alt={t.author}
                    className="w-12 h-12 rounded-full object-cover border-2 border-cyan-500/30"
                  />
                  <div>
                    <h4 className="text-gray-900 font-extrabold text-base">{t.author}</h4>
                    <p className="text-cyan-600 text-xs font-bold">{t.role}</p>
                    <p className="text-gray-500 text-xs flex items-center gap-1 mt-0.5 font-medium">
                      <Building2 className="w-3 h-3 text-gray-400" />
                      {t.company}
                    </p>
                  </div>
                </div>

                <div className="inline-block bg-slate-100 border border-slate-200 text-slate-700 text-xs px-3 py-1 rounded-full font-semibold">
                  {t.project}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
